import React, { useEffect, useRef, useState, useCallback } from 'react';

// --- Constants ---
const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 600;
const MAX_DEPTH = 8;
const MIN_DEPTH = 0;
const DEFAULT_DEPTH = 5;
const INITIAL_LINE_WIDTH = 20;
const DEFAULT_GAP = 10;

const CantorSet = () => {
  const canvasRef = useRef(null);
  const [depth, setDepth] = useState(DEFAULT_DEPTH);
  const [lineWidth, setLineWidth] = useState(INITIAL_LINE_WIDTH);
  const [gap, setGap] = useState(DEFAULT_GAP);
  const [colorScheme, setColorScheme] = useState('gradient'); // 'gradient', 'rainbow', 'monochrome'
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef(null);
  const directionRef = useRef(1); // 1: 증가, -1: 감소

  // 칸토어 집합 그리기 함수
  const drawCantorSet = useCallback((ctx, x, y, length, depth, currentDepth = 0) => {
    if (currentDepth > depth) return;
    
    // 색상 설정
    let color;
    if (colorScheme === 'rainbow') {
      // 무지개 색상 (깊이에 따라 다른 색상)
      const hue = (currentDepth * 30) % 360;
      color = `hsl(${hue}, 80%, 60%)`;
    } else if (colorScheme === 'monochrome') {
      // 단색 (깊이에 따라 명도 변화)
      const lightness = 70 - currentDepth * 6;
      color = `hsl(260, 60%, ${lightness}%)`;
    } else {
      // 그라데이션 (위치에 따른 색상 변화)
      const normalizedX = x / CANVAS_WIDTH;
      const hue = 200 + normalizedX * 160;
      color = `hsl(${hue}, 80%, 60%)`;
    }
    
    // 현재 깊이의 선 그리기
    ctx.fillStyle = color;
    ctx.fillRect(x, y, length, lineWidth);

    // 그림자 효과 (더 입체적으로 보이도록)
    ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
    ctx.shadowBlur = 3;
    ctx.shadowOffsetY = 2;
    
    // 다음 깊이로 재귀 호출
    const newY = y + lineWidth + gap;
    const newLength = length / 3;
    
    // 왼쪽 부분
    drawCantorSet(ctx, x, newY, newLength, depth, currentDepth + 1);
    
    // 오른쪽 부분
    drawCantorSet(ctx, x + 2 * newLength, newY, newLength, depth, currentDepth + 1);
  }, [lineWidth, gap, colorScheme]);
  
  // 전체 캔버스 그리기
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // 배경 그리기
    const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
    gradient.addColorStop(0, '#1E1E3F');
    gradient.addColorStop(1, '#2D2B55');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // 외곽선이 있는 경계 박스 그리기
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 1;
    ctx.strokeRect(20, 20, CANVAS_WIDTH - 40, CANVAS_HEIGHT - 40);
    
    // 제목 및 설명 텍스트
    ctx.font = 'bold 20px Arial';
    ctx.fillStyle = '#FFFFFF';
    ctx.textAlign = 'center';
    ctx.fillText('Cantor Set Fractal', CANVAS_WIDTH / 2, 50);
    
    ctx.font = '14px Arial';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.fillText(`Depth: ${depth}`, CANVAS_WIDTH / 2, 80);
    
    // 칸토어 집합 그리기 (시작점은 왼쪽 여백에서 시작, 길이는 캔버스 폭에서 여백 제외)
    const startX = 50;
    const startY = 120;
    const cantorLength = CANVAS_WIDTH - 100;
    
    // 그림자 초기화
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    
    // 칸토어 집합 그리기
    drawCantorSet(ctx, startX, startY, cantorLength, depth);
    
    // 수식 표현 (표현식이나 수학적 설명)
    ctx.font = 'italic 14px Arial';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.textAlign = 'right';
    ctx.fillText('C = [0,1] \\ ∪ (1/3 + 2/3·C)', CANVAS_WIDTH - 50, CANVAS_HEIGHT - 30);
  }, [depth, drawCantorSet]);
  
  // 자동 애니메이션 (깊이 증가/감소)
  const animateDepth = useCallback(() => {
    setIsAnimating(true);
    
    const animate = () => {
      setDepth(prevDepth => {
        // 방향 전환 체크
        if (prevDepth >= MAX_DEPTH) {
          directionRef.current = -1;
        } else if (prevDepth <= MIN_DEPTH) {
          directionRef.current = 1;
        }
        
        // 새 깊이 계산
        return prevDepth + directionRef.current;
      });
      
      // 다음 단계 애니메이션 (500ms 간격으로)
      animationRef.current = setTimeout(animate, 800);
    };
    
    animate();
    
    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, []);
  
  // 애니메이션 중지
  const stopAnimation = () => {
    if (animationRef.current) {
      clearTimeout(animationRef.current);
      animationRef.current = null;
    }
    setIsAnimating(false);
  };
  
  // 깊이 변경 핸들러
  const handleDepthChange = (e) => {
    stopAnimation();
    setDepth(parseInt(e.target.value));
  };
  
  // 선 두께 변경 핸들러
  const handleLineWidthChange = (e) => {
    setLineWidth(parseInt(e.target.value));
  };
  
  // 간격 변경 핸들러
  const handleGapChange = (e) => {
    setGap(parseInt(e.target.value));
  };
  
  // 색상 모드 변경 핸들러
  const handleColorSchemeChange = (scheme) => {
    setColorScheme(scheme);
  };
  
  // 애니메이션 토글
  const toggleAnimation = () => {
    if (isAnimating) {
      stopAnimation();
    } else {
      animateDepth();
    }
  };
  
  // 초기화 핸들러
  const handleReset = () => {
    stopAnimation();
    setDepth(DEFAULT_DEPTH);
    setLineWidth(INITIAL_LINE_WIDTH);
    setGap(DEFAULT_GAP);
    setColorScheme('gradient');
  };
  
  // 초기화 및 변경시 다시 그리기
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = CANVAS_WIDTH;
      canvas.height = CANVAS_HEIGHT;
      draw();
    }
    
    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, [draw]);
  
  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>Cantor Set Fractal</h2>
      <div style={canvasContainerStyle}>
        <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} style={canvasStyle} />
      </div>
      
      <div style={controlsStyle}>
        <div style={controlGroupStyle}>
          <label htmlFor="depth" style={labelStyle}>깊이: {depth}</label>
          <input
            id="depth"
            type="range"
            min={MIN_DEPTH}
            max={MAX_DEPTH}
            value={depth}
            onChange={handleDepthChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label htmlFor="lineWidth" style={labelStyle}>선 두께: {lineWidth}px</label>
          <input
            id="lineWidth"
            type="range"
            min={5}
            max={30}
            value={lineWidth}
            onChange={handleLineWidthChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label htmlFor="gap" style={labelStyle}>간격: {gap}px</label>
          <input
            id="gap"
            type="range"
            min={5}
            max={30}
            value={gap}
            onChange={handleGapChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label style={labelStyle}>색상 모드:</label>
          <div style={colorButtonsStyle}>
            <button 
              onClick={() => handleColorSchemeChange('gradient')} 
              style={{
                ...colorButtonStyle, 
                background: colorScheme === 'gradient' 
                  ? 'linear-gradient(to right, #00c9ff, #92fe9d)'
                  : '#555',
                color: 'white'
              }}
            >
              그라데이션
            </button>
            <button 
              onClick={() => handleColorSchemeChange('rainbow')} 
              style={{
                ...colorButtonStyle, 
                background: colorScheme === 'rainbow' 
                  ? 'linear-gradient(to right, violet, indigo, blue, green, yellow, orange, red)'
                  : '#555',
                color: 'white'
              }}
            >
              무지개
            </button>
            <button 
              onClick={() => handleColorSchemeChange('monochrome')} 
              style={{
                ...colorButtonStyle, 
                background: colorScheme === 'monochrome' 
                  ? 'linear-gradient(to right, #8e44ad, #9b59b6, #af7ac5)'
                  : '#555',
                color: 'white'
              }}
            >
              단색
            </button>
          </div>
        </div>
        
        <div style={buttonContainerStyle}>
          <button 
            onClick={toggleAnimation} 
            style={isAnimating ? pauseButtonStyle : playButtonStyle}
          >
            {isAnimating ? '정지' : '자동 변화'}
          </button>
          <button onClick={handleReset} style={resetButtonStyle}>
            리셋
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Styled Components ---
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #1e1e30 100%)',
  color: '#e0e0e0',
  fontFamily: 'Arial, sans-serif'
};

const titleStyle = {
  margin: '0 0 1.5rem 0',
  color: '#ffffff',
  fontSize: '2rem',
  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  letterSpacing: '1px'
};

const canvasContainerStyle = {
  padding: '0.5rem',
  background: 'rgba(255,255,255,0.05)',
  borderRadius: '12px',
  boxShadow: '0 10px 20px rgba(0,0,0,0.3), 0 6px 6px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.1) inset',
  overflow: 'hidden'
};

const canvasStyle = {
  display: 'block',
  borderRadius: '8px',
  boxShadow: '0 0 0 1px rgba(255,255,255,0.05) inset'
};

const controlsStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.2rem',
  marginTop: '1.5rem',
  width: '80%',
  maxWidth: '500px',
  background: 'rgba(255,255,255,0.08)',
  padding: '1.5rem',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
};

const controlGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
};

const labelStyle = {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '#e0e0e0'
};

const sliderStyle = {
  width: '100%',
  height: '8px',
  appearance: 'none',
  background: 'linear-gradient(to right, #633974, #bb8fce)',
  outline: 'none',
  borderRadius: '10px',
  cursor: 'pointer'
};

const buttonContainerStyle = {
  display: 'flex',
  gap: '1rem',
  marginTop: '0.5rem'
};

const colorButtonsStyle = {
  display: 'flex',
  gap: '0.8rem',
  flexWrap: 'wrap'
};

const colorButtonStyle = {
  padding: '0.5rem 1rem',
  fontSize: '0.9rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  border: 'none',
  borderRadius: '6px',
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  outline: 'none'
};

const buttonBaseStyle = {
  flex: 1,
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  border: 'none',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.2)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  outline: 'none'
};

const playButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
  color: 'white'
};

const pauseButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
  color: 'white'
};

const resetButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #7f8c8d 0%, #2c3e50 100%)',
  color: 'white'
};

export default CantorSet; 